class ProductCardGallery extends HTMLElement {
  #mediaQuery = window.matchMedia('(max-width: 699px)');
  #active = false;
  #jumping = false;
  #ignoreMutations = false;
  #logicalIndex = 0;
  #pointerScrollLeft = 0;
  #jumpTimer = 0;
  #scrollTimer = 0;
  #resizeFrame = 0;
  #resizeObserver = null;
  #mutations = null;

  connectedCallback() {
    if (!this.hasAttribute('data-mobile-carousel')) {
      return;
    }

    this.#resizeObserver = new ResizeObserver(this.#onResize);
    this.#mutations = new MutationObserver(this.#onMutate);
    this.#mutations.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset']
    });

    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('click', this.#onClick);
    this.#mediaQuery.addEventListener('change', this.#onMediaChange);

    if ('onscrollend' in window) {
      this.addEventListener('scrollend', this.#onScrollEnd);
    } else {
      this.addEventListener('scroll', this.#onScroll, { passive: true });
    }

    this.#sync();
  }

  disconnectedCallback() {
    this.#mediaQuery.removeEventListener('change', this.#onMediaChange);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('scrollend', this.#onScrollEnd);
    this.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver?.disconnect();
    this.#mutations?.disconnect();
    window.clearTimeout(this.#jumpTimer);
    window.clearTimeout(this.#scrollTimer);
    window.cancelAnimationFrame(this.#resizeFrame);
  }

  #onMediaChange = () => {
    this.#sync();
  };

  #onPointerDown = () => {
    this.#pointerScrollLeft = this.scrollLeft;
  };

  #onClick = (event) => {
    if (Math.abs(this.scrollLeft - this.#pointerScrollLeft) <= 8) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  #onScroll = () => {
    window.clearTimeout(this.#scrollTimer);
    this.#scrollTimer = window.setTimeout(() => this.#onScrollEnd(), 80);
  };

  #onScrollEnd = () => {
    if (!this.#active || this.#jumping) {
      this.#jumping = false;
      return;
    }

    const slide = [...this.children][this.#closestChildIndex()];

    if (!slide) {
      return;
    }

    if (slide.dataset.galleryClone === 'leading') {
      this.#logicalIndex = 1;
      this.#jumpTo(this.#secondaryImage());
      return;
    }

    if (slide.dataset.galleryClone === 'trailing') {
      this.#logicalIndex = 0;
      this.#jumpTo(this.#primaryImage());
      return;
    }

    this.#logicalIndex = slide.classList.contains('product-card__image--primary') ? 0 : 1;
  };

  #onResize = () => {
    if (!this.#active || this.#resizeFrame) {
      return;
    }

    this.#resizeFrame = window.requestAnimationFrame(() => {
      this.#resizeFrame = 0;
      const slide = [...this.children][this.#closestChildIndex()];

      if (!slide) {
        return;
      }

      if (slide.dataset.galleryClone === 'leading') {
        this.#logicalIndex = 1;
        this.#jumpTo(this.#secondaryImage());
        return;
      }

      if (slide.dataset.galleryClone === 'trailing') {
        this.#logicalIndex = 0;
        this.#jumpTo(this.#primaryImage());
        return;
      }

      this.#logicalIndex = slide.classList.contains('product-card__image--primary') ? 0 : 1;
      this.#jumpTo(slide);
    });
  };

  #onMutate = () => {
    if (this.#ignoreMutations || !this.#active) {
      return;
    }

    const primary = this.#primaryImage();
    const secondary = this.#secondaryImage();

    if (!primary || !secondary) {
      this.#disable();
      return;
    }

    this.#copyImage(secondary, this.querySelector('[data-gallery-clone="leading"]'));
    this.#copyImage(primary, this.querySelector('[data-gallery-clone="trailing"]'));
    this.#logicalIndex = 0;
    this.#jumpTo(primary);
  };

  #sync() {
    if (this.#mediaQuery.matches) {
      this.#enable();
      return;
    }

    this.#disable();
  }

  #enable() {
    if (this.#active) {
      const current = this.#logicalIndex === 1 ? this.#secondaryImage() : this.#primaryImage();
      this.#jumpTo(current);
      return;
    }

    const primary = this.#primaryImage();
    const secondary = this.#secondaryImage();

    if (!primary || !secondary) {
      return;
    }

    this.#withIgnoredMutations(() => {
      this.prepend(this.#makeClone(secondary, 'leading'));
      this.append(this.#makeClone(primary, 'trailing'));
    });

    this.#active = true;
    this.#logicalIndex = 0;

    if (this.clientWidth > 0) {
      this.#jumpTo(primary);
    }

    this.#resizeObserver.observe(this);
  }

  #disable() {
    if (!this.#active && !this.querySelector('[data-gallery-clone]')) {
      return;
    }

    this.#resizeObserver?.disconnect();
    this.#withIgnoredMutations(() => {
      this.querySelectorAll('[data-gallery-clone]').forEach((node) => node.remove());
    });
    this.#active = false;
    this.scrollLeft = 0;
  }

  #makeClone(source, position) {
    const clone = source.cloneNode(true);
    clone.classList.remove('product-card__image--primary', 'product-card__image--secondary', 'product-card__image--next');
    clone.classList.add('product-card__gallery-clone');
    clone.dataset.galleryClone = position;
    clone.setAttribute('aria-hidden', 'true');
    clone.alt = '';
    clone.loading = 'eager';
    clone.removeAttribute('fetchpriority');
    return clone;
  }

  #copyImage(from, to) {
    if (!from || !to) {
      return;
    }

    if (to.srcset === from.srcset && to.getAttribute('src') === from.getAttribute('src')) {
      return;
    }

    this.#withIgnoredMutations(() => {
      to.src = from.src;
      to.srcset = from.srcset;
      to.sizes = from.sizes;
      to.alt = '';
    });
  }

  #primaryImage() {
    return this.querySelector('.product-card__image--primary');
  }

  #secondaryImage() {
    return this.querySelector('.product-card__image--secondary, .product-card__image--next');
  }

  #closestChildIndex() {
    const rootLeft = this.getBoundingClientRect().left;
    let closest = 0;
    let min = Number.POSITIVE_INFINITY;

    [...this.children].forEach((child, index) => {
      const delta = Math.abs(child.getBoundingClientRect().left - rootLeft);

      if (delta < min) {
        min = delta;
        closest = index;
      }
    });

    return closest;
  }

  #alignTo(slide) {
    if (!slide) {
      return false;
    }

    const delta = slide.getBoundingClientRect().left - this.getBoundingClientRect().left;

    if (Math.abs(delta) < 1) {
      return false;
    }

    const previousSnap = this.style.scrollSnapType;
    this.style.scrollSnapType = 'none';
    this.scrollLeft += delta;
    this.style.scrollSnapType = previousSnap;
    return true;
  }

  #jumpTo(slide) {
    if (!this.#alignTo(slide)) {
      return;
    }

    this.#jumping = true;
    window.clearTimeout(this.#jumpTimer);
    this.#jumpTimer = window.setTimeout(() => {
      this.#jumping = false;
    }, 50);
  }

  #withIgnoredMutations(callback) {
    this.#ignoreMutations = true;
    callback();
    queueMicrotask(() => {
      this.#ignoreMutations = false;
    });
  }
}

if (!window.customElements.get('product-card-gallery')) {
  window.customElements.define('product-card-gallery', ProductCardGallery);
}
